import { CommonModule } from '@angular/common';
import { Component, OnInit, forwardRef, Input } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { FormsModule } from '@angular/forms';
import { SelectItem } from 'primeng/api';
import { StringTypes } from '../../../../models/wellInfo';
import { CTWOTubingODs, CTWOTubingOD, CasingOrTubingId, CasingOrTubingDirection } from '../../../../models/models';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { FieldsetModule } from 'primeng/fieldset';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { VendorJob, WellInfo } from '../../../../services/nswag/nswagclient';
import { CasingTubingComponent } from '../../casing-tubing/casing-tubing.component';
import { PlugsComponent } from '../../plugs/plugs.component';

@Component({
  selector: 'app-job-well-information',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    DialogModule,
    FieldsetModule,
    InputNumberModule,
    InputTextModule,
    SelectModule,
    CasingTubingComponent,
    PlugsComponent
  ],
  templateUrl: './job-well-information.component.html',
  styleUrls: ['./job-well-information.component.scss'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => JobWellInformationComponent),
    multi: true
  }]
})
export class JobWellInformationComponent implements OnInit, ControlValueAccessor {

  private _onTouched: () => void = () => {};

  @Input() _job: VendorJob = new VendorJob();
  get job() {
    return this._job;
  }
  set job(val: VendorJob | undefined) {
    this._job = val ?? new VendorJob();
    this.ensureWellInfo();
    this.propagateChange(this._job);
  }
  @Input() modelInvalid = false;
  @Input() disabled = false;

  stringTypes: SelectItem[] = [];
  ctwoTubingODs: SelectItem[] = [];
  hozTubingODs: SelectItem[] = [];

  selectedCTWOTubingOD: CTWOTubingOD | null = null;
  selectedHozTubingOD: CTWOTubingOD | null = null;

  showCasingInvalidDialog = false;

  constructor() { }

  ngOnInit() {
    this.setupDrowDownOptions();
    this.ensureWellInfo();
  }

  /****** ControlValueAccessor methods to handle ngModel read and updates ******/
  propagateChange = (_: any) => {};

  writeValue(obj: any): void {
    if (obj) {
      this._job = obj;
      this.ensureWellInfo();
      this.setSelectedTubingODs();
    }
  }

  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any): void {
    this._onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  setSelectedTubingODs() {
    const wellInfo = this.ensureWellInfo();
    this.selectedCTWOTubingOD = { id: wellInfo.ctWoTubingOdId, value: wellInfo.ctWoTubingOd };
    this.selectedHozTubingOD = { id: wellInfo.hozTubingOdId, value: wellInfo.hozTubingOd };
  }

  /****** Componet UI control methods ******/
  setupDrowDownOptions() {
    this.stringTypes = [
      { label: StringTypes.coilTubing, value: StringTypes.coilTubing },
      { label: StringTypes.jointTubing, value: StringTypes.jointTubing }
    ];
    this.ctwoTubingODs = this.getCTWOTubingODs();
    this.hozTubingODs = this.ctwoTubingODs;
  }

  getCTWOTubingODs(): SelectItem[] {
    const tubingODs: SelectItem[] = CTWOTubingODs;
    return tubingODs.sort((a, b) => {
      const left = String(a.label ?? '');
      const right = String(b.label ?? '');
      if (left < right) { return -1; }
      if (left > right) { return 1; }
      return 0;
    });
  }

  setLateralLengthEst() {
    const wellInfo = this.ensureWellInfo();
    if (wellInfo.tvd && wellInfo.md) {
      wellInfo.lateralLengthEst = wellInfo.md - wellInfo.tvd;
    }
  }

  setCTWOTubingOD() {
    if (!this.selectedCTWOTubingOD) {
      return;
    }
    const wellInfo = this.ensureWellInfo();
    wellInfo.ctWoTubingOd = this.selectedCTWOTubingOD.value;
    wellInfo.ctWoTubingOdId = this.selectedCTWOTubingOD.id;
  }

  setHozTubingOD() {
    if (!this.selectedHozTubingOD) {
      return;
    }
    const wellInfo = this.ensureWellInfo();
    wellInfo.hozTubingOd = this.selectedHozTubingOD.value;
    wellInfo.hozTubingOdId = this.selectedHozTubingOD.id;
  }

  updateCsgId(csgId: number | undefined) {
    const wellInfo = this.ensureWellInfo();
    wellInfo.csgId = csgId;
  }

  validateIdSelected(casingOrTubingId: CasingOrTubingId) {
    const wellInfo = this.ensureWellInfo();
    const selectedCtWoTubingOd = this.selectedCTWOTubingOD?.value;
    const selectedHozTubingOd = this.selectedHozTubingOD?.value;

    // Coil Tubing selection: all casingOrTubing ID's must be
    // larger than selectedCtWoTubing ODs
    // Jointed Tubing selection: selectedCtWoTubingOd must be
    // smaller than the vertical casing's ID and hozTubingOd must
    // be smaller than the horizontal casing's ID.
    // If Production Tubing is the casingOrTubingId selected,
    // it must be larger than selectedCtWoTubingOD in either Coiled or Jointed
    if ((!wellInfo.stringType || (wellInfo.stringType === StringTypes.coilTubing)) &&
        casingOrTubingId) {
        if (selectedCtWoTubingOd != null && casingOrTubingId.value != null && selectedCtWoTubingOd >= casingOrTubingId.value) {
            this.showCasingInvalidDialog = true;
        }
    } else if (wellInfo.stringType === StringTypes.jointTubing &&
               casingOrTubingId) {
                 switch (casingOrTubingId.casingOrTubingType) {
                   case CasingOrTubingDirection.vertical: {
                    if (selectedCtWoTubingOd != null && casingOrTubingId.value != null && selectedCtWoTubingOd >= casingOrTubingId.value) {
                      this.showCasingInvalidDialog = true;
                    }
                    break;
                   }
                   case CasingOrTubingDirection.horizontal: {
                    if (selectedHozTubingOd != null && casingOrTubingId.value != null && selectedHozTubingOd >= casingOrTubingId.value) {
                      this.showCasingInvalidDialog = true;
                    }
                    break;
                   }
                   case CasingOrTubingDirection.tubing: {
                    if (selectedCtWoTubingOd != null && casingOrTubingId.value != null && selectedCtWoTubingOd >= casingOrTubingId.value) {
                      this.showCasingInvalidDialog = true;
                    }
                    break;
                   }
                   default: { break; }
                 }
    }
  }

  closeCasingInvalidDialog() {
    this.showCasingInvalidDialog = false;
  }

  private ensureWellInfo(): WellInfo {
    if (!this._job.wellInfo) {
      this._job.wellInfo = new WellInfo();
    }

    return this._job.wellInfo;
  }
}
